import {
  Alert,
  AlertItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeading,
  TableRow
} from '@vmware-react/clarity';
import React, { useEffect, useState, useCallback } from 'react';
import { ProductApi, RequestApi } from 'vrlcm-gui-api';
import {
  LCOPS_REQUEST_STATUS_PAGE_URL,
  PRODUCT_NAME_CONSTANTS
} from 'vrlcm-gui-constants';
import {
  AsyncOpState,
  AsyncOpStateWithError,
  RequestState
} from 'vrlcm-gui-types';
import { extractApiErrorMessageFromAxiosErr } from 'vrlcm-gui-utils';
import { l10n } from '../with-app-root/i18n/i18nUtil';
import LinkStyledButton from '../link-styled-button/LinkStyledButton';

interface Props {
  environmentId: string;
  productId: string;
  showInventorySyncTable?: boolean;
  inventorySyncProgress: (state: AsyncOpState) => void;
  tenantId: string;
}

export const PreActionInventorySync: React.SFC<Props> = props => {
  const {
    environmentId,
    productId,
    showInventorySyncTable = true,
    inventorySyncProgress,
    tenantId
  } = props;

  const [inventorySyncState, setInventorySyncState] = useState<
    AsyncOpStateWithError
  >({ state: AsyncOpState.NotStarted });
  const [requestId, setRequestId] = useState<string>();

  const waitForRequestCompletion = useCallback(async () => {
    try {
      if (requestId) {
        const request = await RequestApi.pollRequestForCompletion(
          requestId,
          5000
        );
        if (request.state !== RequestState.COMPLETED) {
          const error = (
            <>
              <span
                dangerouslySetInnerHTML={{
                  __html: l10n('components.preActionInvSync.errorMessage', [
                    LCOPS_REQUEST_STATUS_PAGE_URL(requestId)
                  ])
                }}
              />
              <LinkStyledButton onClick={waitForRequestCompletion}>
                {l10n('components.preActionInvSync.refresh')}
              </LinkStyledButton>{' '}
              {l10n('components.preActionInvSync.proceed')}
            </>
          );
          setInventorySyncState({ state: AsyncOpState.Failed, error });
        } else {
          setInventorySyncState({ state: AsyncOpState.Completed });
        }
      } else {
        throw Error;
      }
    } catch {
      setInventorySyncState({
        state: AsyncOpState.Failed,
        error: l10n('fetchInvSyncFailed')
      });
    }
  }, [requestId]);

  useEffect(() => {
    if (requestId) {
      waitForRequestCompletion();
    }
  }, [requestId, waitForRequestCompletion]);

  useEffect(() => {
    inventorySyncProgress(inventorySyncState.state);
  }, [inventorySyncState.state, inventorySyncProgress]);

  const onClickTriggerInventorySync = async () => {
    try {
      setInventorySyncState({ state: AsyncOpState.Inprogress });
      const id = await ProductApi.triggerProductInventorySync(
        environmentId,
        productId,
        tenantId
      );
      setRequestId(id);
    } catch (e) {
      const error = (
        <>
          {l10n('triggerInvSyncFailed')}
          {extractApiErrorMessageFromAxiosErr(e)}
        </>
      );
      setInventorySyncState({ state: AsyncOpState.Failed, error });
    }
  };

  return (
    <div>
      {showInventorySyncTable ? (
        <Table orientation="vertical">
          <TableBody>
            <TableRow>
              <TableHeading>
                {l10n('components.preActionInvSync.invSyncMandatory', [
                  PRODUCT_NAME_CONSTANTS.lcm
                ])}
              </TableHeading>
              <TableCell>
                <Button
                  size="sm"
                  disabled={
                    inventorySyncState.state === AsyncOpState.Inprogress
                  }
                  onClick={onClickTriggerInventorySync}
                >
                  {inventorySyncState.state === AsyncOpState.Inprogress && (
                    <span className="spinner spinner-inline" />
                  )}
                  {l10n('triggerInvSync')}
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      ) : (
        <Alert
          type="info"
          ariaLiveMsg={l10n('components.preActionInvSync.adviseAlert')}
          closable={true}
          items={[
            <AlertItem>
              <div className="alert-inventory-sync">
                <span>{l10n('components.preActionInvSync.adviseAlert')}</span>
                <button
                  disabled={
                    inventorySyncState.state === AsyncOpState.Inprogress
                  }
                  onClick={onClickTriggerInventorySync}
                >
                  {l10n('triggerInvSync')}
                </button>
                {inventorySyncState.state === AsyncOpState.Inprogress && (
                  <span className="spinner spinner-inline" />
                )}
              </div>
            </AlertItem>
          ]}
        />
      )}
      {inventorySyncState.state === AsyncOpState.Failed &&
        inventorySyncState.error && (
          <Alert
            ariaLiveMsg={`${inventorySyncState.error}`}
            items={[<AlertItem>{inventorySyncState.error}</AlertItem>]}
            type="danger"
          />
        )}
      {inventorySyncState.state === AsyncOpState.Inprogress && requestId && (
        <Alert
          ariaLiveMsg={l10n('components.preActionInvSync.doNotQuitWindowText')}
          items={[
            <AlertItem>
              <span
                dangerouslySetInnerHTML={{
                  __html: l10n(
                    'components.preActionInvSync.doNotQuitWindowHTML',
                    [LCOPS_REQUEST_STATUS_PAGE_URL(requestId)]
                  )
                }}
              />
            </AlertItem>
          ]}
        />
      )}
      {inventorySyncState.state === AsyncOpState.Completed && (
        <Alert
          ariaLiveMsg={l10n('components.preActionInvSync.invSyncComplete')}
          closable={!showInventorySyncTable}
          items={[
            <AlertItem>
              {l10n('components.preActionInvSync.invSyncComplete')}
            </AlertItem>
          ]}
          type="success"
        />
      )}
    </div>
  );
};
